/**
 * IMAP Config Step
 * Step 2C: Configure IMAP/SMTP settings
 */
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { Button } from "@/components/base/buttons/button";
import { ArrowLeft, CheckCircle, XCircle, Server01, Lock01 } from "@untitledui/icons";
import {
    useImapProviderPresets,
    useCreateImapEmailAccount,
    useTestEmailConnection,
} from "@/hooks/api/use-email-accounts";
import { toast } from "@/lib/toast-service";

interface ImapConfigStepProps {
    email: string;
    displayName?: string;
    onBack: () => void;
    onSuccess: () => void;
}

export const ImapConfigStep = ({
    email,
    displayName,
    onBack,
    onSuccess,
}: ImapConfigStepProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    // Fetch provider presets
    const { data: presets = [], isLoading: presetsLoading } = useImapProviderPresets();
    const createAccountMutation = useCreateImapEmailAccount();
    const testConnectionMutation = useTestEmailConnection();

    // Form state
    const [selectedPreset, setSelectedPreset] = useState<string>("");
    const [imapHost, setImapHost] = useState("");
    const [imapPort, setImapPort] = useState("993");
    const [imapUseSsl, setImapUseSsl] = useState(true);
    const [smtpHost, setSmtpHost] = useState("");
    const [smtpPort, setSmtpPort] = useState("465");
    const [smtpUseSsl, setSmtpUseSsl] = useState(true);
    const [username, setUsername] = useState(email);
    const [password, setPassword] = useState("");
    const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "failed">("idle");
    const [testError, setTestError] = useState<string | null>(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [createdAccountId, setCreatedAccountId] = useState<string | null>(null);

    // Build select items from presets
    const presetItems = useMemo(() => {
        const items = [
            { id: "", label: t("imapConfig.presetPlaceholder") },
            ...presets.map((preset) => ({
                id: preset.name,
                label: preset.display_name,
            })),
            { id: "custom", label: t("imapConfig.customOption") },
        ];
        return items;
    }, [presets, t]);

    // Apply preset when selected
    useEffect(() => {
        if (selectedPreset && selectedPreset !== "custom") {
            const preset = presets.find((p) => p.name === selectedPreset);
            if (preset) {
                setImapHost(preset.imap_host);
                setImapPort(String(preset.imap_port));
                setImapUseSsl(preset.imap_use_ssl);
                setSmtpHost(preset.smtp_host);
                setSmtpPort(String(preset.smtp_port));
                setSmtpUseSsl(preset.smtp_use_ssl);
            }
        }
    }, [selectedPreset, presets]);

    // Try to auto-detect preset from email domain
    // Note: ProviderPreset doesn't have a domains field, so we skip auto-detection
    // Users will need to manually select a preset if needed

    const handleTestConnection = async () => {
        setTestStatus("testing");
        setTestError(null);

        try {
            // First create the account if not already created
            let accountId = createdAccountId;
            if (!accountId) {
                const account = await createAccountMutation.mutateAsync({
                    email_address: email,
                    display_name: displayName || undefined,
                    imap_host: imapHost,
                    imap_port: parseInt(imapPort),
                    imap_use_ssl: imapUseSsl,
                    smtp_host: smtpHost,
                    smtp_port: parseInt(smtpPort),
                    smtp_use_ssl: smtpUseSsl,
                    imap_username: username,
                    imap_password: password,
                });
                accountId = account.account_id;
                setCreatedAccountId(accountId);
            }

            // Test the connection
            const result = await testConnectionMutation.mutateAsync(accountId);

            if (result.success) {
                setTestStatus("success");
            } else {
                setTestStatus("failed");
                setTestError(result.error_message || t("imapConfig.testFailed"));
            }
        } catch (error: unknown) {
            setTestStatus("failed");
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            setTestError(t("imapConfig.testFailedMessage", { error: errorMessage }));
        }
    };

    const handleConnect = async () => {
        setIsConnecting(true);

        try {
            // If we haven't created the account yet, create it now
            if (!createdAccountId) {
                await createAccountMutation.mutateAsync({
                    email_address: email,
                    display_name: displayName || undefined,
                    imap_host: imapHost,
                    imap_port: parseInt(imapPort),
                    imap_use_ssl: imapUseSsl,
                    smtp_host: smtpHost,
                    smtp_port: parseInt(smtpPort),
                    smtp_use_ssl: smtpUseSsl,
                    imap_username: username,
                    imap_password: password,
                });
            }

            onSuccess();
        } catch (error) {
            toast.error(t("errors.createAccount"));
        } finally {
            setIsConnecting(false);
        }
    };

    const isFormValid = imapHost && imapPort && smtpHost && smtpPort && username && password;

    return (
        <div className="space-y-6">
            {/* Title */}
            <div>
                <h3 className="text-base font-medium text-primary">
                    {t("imapConfig.title")}
                </h3>
                <p className="mt-1 text-sm text-tertiary">
                    {t("imapConfig.description")}
                </p>
            </div>

            {/* Provider Preset Selector */}
            <Select
                label={t("imapConfig.presetLabel")}
                selectedKey={selectedPreset}
                onSelectionChange={(key) => setSelectedPreset(key as string)}
                items={presetItems}
                placeholder={t("imapConfig.presetPlaceholder")}
                isDisabled={presetsLoading}
            >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>

            {/* IMAP Settings */}
            <div className="space-y-4 rounded-lg ring-1 ring-secondary p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Server01 className="h-4 w-4" />
                    {t("imapConfig.imapSection")}
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <Input
                            label={t("imapConfig.imapHost")}
                            value={imapHost}
                            onChange={(value) => setImapHost(value)}
                            placeholder={t("imapConfig.imapHostPlaceholder")}
                        />
                    </div>
                    <Input
                        type="number"
                        label={t("imapConfig.imapPort")}
                        value={imapPort}
                        onChange={(value) => setImapPort(value)}
                    />
                </div>
                <Toggle
                    label={t("imapConfig.imapUseSsl")}
                    isSelected={imapUseSsl}
                    onChange={setImapUseSsl}
                />
            </div>

            {/* SMTP Settings */}
            <div className="space-y-4 rounded-lg ring-1 ring-secondary p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Server01 className="h-4 w-4" />
                    {t("imapConfig.smtpSection")}
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <Input
                            label={t("imapConfig.smtpHost")}
                            value={smtpHost}
                            onChange={(value) => setSmtpHost(value)}
                            placeholder={t("imapConfig.smtpHostPlaceholder")}
                        />
                    </div>
                    <Input
                        type="number"
                        label={t("imapConfig.smtpPort")}
                        value={smtpPort}
                        onChange={(value) => setSmtpPort(value)}
                    />
                </div>
                <Toggle
                    label={t("imapConfig.smtpUseSsl")}
                    isSelected={smtpUseSsl}
                    onChange={setSmtpUseSsl}
                />
            </div>

            {/* Credentials */}
            <div className="space-y-4 rounded-lg ring-1 ring-secondary p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Lock01 className="h-4 w-4" />
                    {t("imapConfig.credentialsSection")}
                </div>
                <Input
                    label={t("imapConfig.username")}
                    value={username}
                    onChange={(value) => setUsername(value)}
                    placeholder={t("imapConfig.usernamePlaceholder")}
                />
                <Input
                    type="password"
                    label={t("imapConfig.password")}
                    value={password}
                    onChange={(value) => setPassword(value)}
                    placeholder={t("imapConfig.passwordPlaceholder")}
                    hint={t("imapConfig.appPasswordHint")}
                />
            </div>

            {/* Test Connection Result */}
            {testStatus === "success" && (
                <div className="flex items-center gap-2 rounded-lg bg-success-primary p-3 text-sm text-fg-success-primary">
                    <CheckCircle className="h-4 w-4" />
                    {t("imapConfig.testSuccess")}
                </div>
            )}
            {testStatus === "failed" && (
                <div className="flex items-center gap-2 rounded-lg bg-error-primary p-3 text-sm text-fg-error-primary">
                    <XCircle className="h-4 w-4" />
                    {testError || t("imapConfig.testFailed")}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4">
                <Button
                    onClick={onBack}
                    isDisabled={isConnecting || testStatus === "testing"}
                    color="tertiary"
                    size="sm"
                    iconLeading={ArrowLeft}
                >
                    {t("actions.back")}
                </Button>

                <div className="flex gap-3">
                    <Button
                        onClick={handleTestConnection}
                        isDisabled={!isFormValid || testStatus === "testing" || isConnecting}
                        isLoading={testStatus === "testing"}
                        color="secondary"
                        size="sm"
                    >
                        {testStatus === "testing" ? t("imapConfig.testing") : t("imapConfig.testConnection")}
                    </Button>
                    <Button
                        onClick={handleConnect}
                        isDisabled={!isFormValid || isConnecting}
                        isLoading={isConnecting}
                        color="primary"
                        size="sm"
                    >
                        {isConnecting ? t("imapConfig.connecting") : t("actions.connect")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
